/**
 * Pure feature flag logic — no React, no Firebase.
 *
 * Flags control which tabs are visible in TradesBeam.
 * God-mode users always bypass flags and see everything.
 */

import { GOD_MODE_EMAILS } from './authHelpers';

// ─── Flag Definitions ───────────────────────────────────────────────────────

/** Default state for every flag — all enabled (backwards compatible). */
export const DEFAULT_FEATURE_FLAGS = {
  home: true,
  locks: true,
  inspect: true,
  receipts: true,
  consult: true,
  profile: true,
};

/** Human-readable metadata for the admin UI. */
export const FEATURE_FLAG_DEFINITIONS = [
  { key: 'home',     label: 'Home',     description: 'Dashboard with WO briefings and quick actions' },
  { key: 'locks',    label: 'Locks',    description: 'Lock code management and change tracking' },
  { key: 'inspect',  label: 'Inspect',  description: 'Property inspection checklists and photos' },
  { key: 'receipts', label: 'Receipts', description: 'Receipt capture and expense tracking' },
  { key: 'consult',  label: 'Consult',  description: 'AI repair consultation assistant' },
  { key: 'profile',  label: 'Profile',  description: 'User profile and preferences' },
];

// ─── Pure Functions ─────────────────────────────────────────────────────────

/**
 * Merge Firestore data with defaults.
 * Missing keys fall back to DEFAULT_FEATURE_FLAGS.
 * Extra keys in Firestore are ignored.
 */
export function resolveFlags(firestoreData) {
  if (!firestoreData || typeof firestoreData !== 'object') {
    return { ...DEFAULT_FEATURE_FLAGS };
  }

  const resolved = {};
  for (const key of Object.keys(DEFAULT_FEATURE_FLAGS)) {
    resolved[key] = typeof firestoreData[key] === 'boolean'
      ? firestoreData[key]
      : DEFAULT_FEATURE_FLAGS[key];
  }
  return resolved;
}

/**
 * Returns the first enabled tab key — used as initial route.
 * Falls back to 'profile' if nothing else is enabled.
 */
export function getDefaultTab(flags) {
  const priority = ['home', 'locks', 'inspect', 'receipts', 'consult', 'profile'];
  for (const key of priority) {
    if (flags[key]) return key;
  }
  return 'profile'; // ultimate fallback — profile should always be on
}

/**
 * Filter a TAB_DEFINITIONS array by enabled flags.
 * Each tab object must have a `key` that matches a flag name.
 */
export function getEnabledTabs(allTabs, flags) {
  if (!allTabs || !flags) return [];
  return allTabs.filter(tab => flags[tab.key] === true);
}

/**
 * God-mode users see everything regardless of flags.
 */
export function shouldBypassFlags(userProfile, godModeEmails = GOD_MODE_EMAILS) {
  if (!userProfile?.email) return false;
  return godModeEmails.includes(userProfile.email.toLowerCase());
}
